import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useProfileComplete } from "@/hooks/useProfileComplete";
import { lazy, Suspense } from "react";
import Layout from "@/components/Layout";
import { Loader2 } from "lucide-react";

const Auth = lazy(() => import("@/pages/Auth"));
const AssetDetail = lazy(() => import("@/pages/AssetDetail"));
const Management = lazy(() => import("@/pages/Management"));
const UserRoles = lazy(() => import("@/pages/UserRoles"));

const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const AuthLog = lazy(() => import("@/pages/AuthLog"));
const Assets = lazy(() => import("@/pages/Assets"));
const Future = lazy(() => import("@/pages/Future"));
const FutureDetail = lazy(() => import("@/pages/FutureDetail"));
const ProfileSetup = lazy(() => import("@/pages/ProfileSetup"));
const Profile = lazy(() => import("@/pages/Profile"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Install = lazy(() => import("@/pages/Install"));
const UserProfileView = lazy(() => import("@/pages/UserProfileView"));
const Permissions = lazy(() => import("@/pages/Permissions"));
const Issues = lazy(() => import("@/pages/Issues"));

const SuspenseLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { isComplete, loading: profileLoading } = useProfileComplete();

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (isComplete === false) return <Navigate to="/profile-setup" replace />;
  return <Layout>{children}</Layout>;
};

const ProfileSetupRoute = () => {
  const { user, loading } = useAuth();
  const { isComplete, loading: profileLoading } = useProfileComplete();

  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return <ProfileSetup />;
};

const AuthRoute = () => {
  const { user, loading } = useAuth();
  const { landingPage, loading: permLoading } = usePermissions();
  if (loading || (user && permLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to={landingPage} replace />;
  return <Auth />;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  const { landingPage, loading: permLoading } = usePermissions();
  if (loading || (user && permLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  return <Navigate to={user ? landingPage : "/auth"} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<SuspenseLoader />}>
            <Routes>
              <Route path="/auth" element={<AuthRoute />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/profile-setup" element={<ProfileSetupRoute />} />
              
              <Route path="/" element={<RootRedirect />} />
              <Route path="/asset/:displayId" element={<ProtectedRoute><AssetDetail /></ProtectedRoute>} />
              <Route path="/management" element={<ProtectedRoute><Management /></ProtectedRoute>} />
              <Route path="/activity" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
              <Route path="/auth-log" element={<ProtectedRoute><AuthLog /></ProtectedRoute>} />
              <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
              <Route path="/future" element={<ProtectedRoute><Future /></ProtectedRoute>} />
              <Route path="/future/:id" element={<ProtectedRoute><FutureDetail /></ProtectedRoute>} />
              <Route path="/issues" element={<ProtectedRoute><Issues /></ProtectedRoute>} />
              <Route path="/roles" element={<ProtectedRoute><UserRoles /></ProtectedRoute>} />
              <Route path="/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
              <Route path="/user/:userId" element={<ProtectedRoute><UserProfileView /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/install" element={<Install />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
